import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';
import ToggleSwitch from '../../../../components/ToggleSwitch';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import { notify } from '../../../../utils/notification';
import {
    createSmtpConfigReq,
    updateSmtpConfigReq,
    smtpConfigErrorMessage,
} from '../../../../axios/request/SocietyController/CtrlSmtpConfigReqs/CtrlSmtpConfigReqs';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export const ENCRYPTION_PORTS = { STARTTLS: 587, SSL: 465, NONE: 25 };

const ENCRYPTION_OPTIONS = [
    { value: 'STARTTLS', label: 'STARTTLS' },
    { value: 'SSL', label: 'SSL' },
    { value: 'NONE', label: 'None' },
];

const EMPTY = {
    config_name: '', host: '', port: 587, encryption: 'STARTTLS', username: '', password: '',
    from_email: '', from_name: '', reply_to_email: '', is_default: 1, is_active: 1,
};

// Everything the PUT accepts except password, which is diffed separately.
const EDITABLE = [
    'config_name', 'host', 'port', 'encryption', 'username',
    'from_email', 'from_name', 'reply_to_email', 'is_default', 'is_active',
];
const NULLABLE = ['from_name', 'reply_to_email'];

// Trimmed form → API body shape (port as a number, blank optionals as null).
const normalize = (form) => {
    const out = {};
    EDITABLE.forEach((k) => {
        let v = form[k];
        if (typeof v === 'string') v = v.trim();
        if (NULLABLE.includes(k) && v === '') v = null;
        if (k === 'port') v = Number(v);
        out[k] = v;
    });
    return out;
};

// Only changed fields; password only when the user typed one ("" means keep it).
const diffSmtpConfig = (original, form) => {
    const next = normalize(form);
    const body = {};
    EDITABLE.forEach((k) => {
        const before = NULLABLE.includes(k) ? (original?.[k] ?? null) : original?.[k];
        if (next[k] !== before) body[k] = next[k];
    });
    if (form.password) body.password = form.password;
    return body;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (form, isEdit) => {
    const e = {};
    if (!form.config_name.trim()) e.config_name = 'Name is required';
    if (!form.host.trim()) e.host = 'SMTP host is required';
    const port = Number(form.port);
    if (!Number.isInteger(port) || port < 1 || port > 65535) e.port = 'Port must be 1–65535';
    if (!form.username.trim()) e.username = 'Username is required';
    if (!isEdit && !form.password) e.password = 'Password is required';
    if (!form.from_email.trim()) e.from_email = 'From email is required';
    else if (!EMAIL_RE.test(form.from_email.trim())) e.from_email = 'Enter a valid email';
    if (form.reply_to_email.trim() && !EMAIL_RE.test(form.reply_to_email.trim())) e.reply_to_email = 'Enter a valid email';
    return e;
};

const inputCls = (hasError) => `tw-w-full tw-h-9 tw-px-3 tw-rounded-md tw-text-xs tw-text-gray-800 tw-bg-white tw-border ${hasError ? 'tw-border-red-400 focus:tw-ring-red-200' : 'tw-border-gray-200 focus:tw-ring-rose-100 focus:tw-border-rose-300'} focus:tw-outline-none focus:tw-ring-2 disabled:tw-bg-gray-50`;

const Field = ({ label, required, error, hint, children, className = '' }) => (
    <div className={className}>
        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">
            {label}{required && <span className="tw-text-red-500"> *</span>}
        </label>
        {children}
        {error
            ? <p className="tw-text-[11px] tw-text-red-500 tw-mt-1 tw-mb-0">{error}</p>
            : hint && <p className="tw-text-[11px] tw-text-gray-400 tw-mt-1 tw-mb-0">{hint}</p>}
    </div>
);

/**
 * Add / edit one SMTP account. Create POSTs the whole form; edit PUTs only the
 * diff, so an untouched password box keeps the saved password.
 */
const SmtpConfigFormModal = ({ open, societyId, config, onClose, onSaved }) => {
    const isEdit = !!config;
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [portTouched, setPortTouched] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(config
            // Saving from this form always makes the account the default.
            ? { ...EDITABLE.reduce((acc, k) => ({ ...acc, [k]: config[k] ?? '' }), { password: '' }), is_default: 1 }
            : EMPTY);
        setErrors({});
        // An existing account's port is a deliberate choice — don't overwrite it.
        setPortTouched(!!config);
        setShowPassword(false);
    }, [open, config]);

    const diff = useMemo(() => (isEdit ? diffSmtpConfig(config, form) : null), [isEdit, config, form]);
    const unchanged = isEdit && Object.keys(diff).length === 0;

    const set = (key, value) => {
        setForm((p) => ({ ...p, [key]: value }));
        setErrors((p) => ({ ...p, [key]: undefined }));
    };

    const setEncryption = (value) => {
        setForm((p) => ({ ...p, encryption: value, port: portTouched ? p.port : ENCRYPTION_PORTS[value] }));
    };

    const handleSubmit = async () => {
        const found = validate(form, isEdit);
        if (Object.keys(found).length) { setErrors(found); return; }
        if (unchanged) return;

        setSaving(true);
        try {
            const body = isEdit ? diff : { ...normalize(form), password: form.password };
            const res = isEdit
                ? await updateSmtpConfigReq(societyId, config.uuid, body)
                : await createSmtpConfigReq(societyId, body);
            if (res?.success === false) throw new Error(res?.message);
            notify('success', res?.message || (isEdit ? 'Email account updated' : 'Email account added'));
            onSaved(res?.data, { isEdit, defaultChanged: 'is_default' in body });
        } catch (err) {
            const status = err?.response?.status;
            const message = smtpConfigErrorMessage(err);
            if (status === 409) {
                setErrors({ config_name: 'An account with this name already exists' });
            } else {
                // Validation messages name the field: body: "from_email" must be …
                const field = status === 400 && /"(\w+)"/.exec(err?.response?.data?.message || '')?.[1];
                if (field && field in EMPTY) setErrors({ [field]: message.replace(/"\w+"\s*/, '').replace(/^\w/, (c) => c.toUpperCase()) });
                else notify('error', message);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        // disableEnforceFocus: SearchableDropdown portals its search box to <body>,
        // outside the dialog's focus trap.
        <Dialog open={open} onClose={saving ? undefined : onClose} TransitionComponent={Transition} disableEnforceFocus
            maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
            <DialogTitle component="div" sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider',
            }}>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <Icon icon={isEdit ? 'lucide:edit' : 'mdi:email-plus-outline'} className="tw-text-base tw-text-rose-500" />
                    <div className="tw-text-sm tw-font-semibold tw-text-gray-800">
                        {isEdit ? 'Edit Email Account' : 'Add Email Account'}
                    </div>
                </div>
                <IconButton aria-label="close" onClick={onClose} disabled={saving} size="small" sx={{ color: 'text.secondary', p: 0.5 }}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 2, py: 2, '&.MuiDialogContent-root': { pt: 2 } }}>
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                    <Field label="Name" required error={errors.config_name} hint="e.g. Billing, Notices" className="sm:tw-col-span-2">
                        <input className={inputCls(errors.config_name)} maxLength={100} disabled={saving}
                            value={form.config_name} onChange={(e) => set('config_name', e.target.value)} />
                    </Field>

                    <Field label="SMTP host" required error={errors.host} className="sm:tw-col-span-2">
                        <input className={inputCls(errors.host)} maxLength={255} disabled={saving} placeholder="smtp.gmail.com"
                            value={form.host} onChange={(e) => set('host', e.target.value)} />
                    </Field>

                    <Field label="Encryption" error={errors.encryption}>
                        <SearchableDropdown small clearable={false} disabled={saving}
                            options={ENCRYPTION_OPTIONS} value={form.encryption}
                            onChange={setEncryption} placeholder="Select encryption" />
                    </Field>

                    <Field label="Port" required error={errors.port}>
                        <input type="number" min={1} max={65535} className={inputCls(errors.port)} disabled={saving}
                            value={form.port} onChange={(e) => { setPortTouched(true); set('port', e.target.value); }} />
                    </Field>

                    <Field label="Username" required error={errors.username}>
                        <input className={inputCls(errors.username)} maxLength={255} disabled={saving} autoComplete="off"
                            value={form.username} onChange={(e) => set('username', e.target.value)} />
                    </Field>

                    <Field label="Password" required={!isEdit} error={errors.password}
                        hint={isEdit ? 'Leave blank to keep the saved password' : undefined}>
                        <div className="tw-relative">
                            <input type={showPassword ? 'text' : 'password'} className={`${inputCls(errors.password)} tw-pr-9`}
                                maxLength={500} disabled={saving} autoComplete="new-password"
                                placeholder={isEdit ? '•••••••• (unchanged)' : ''}
                                value={form.password} onChange={(e) => set('password', e.target.value)} />
                            <button type="button" tabIndex={-1} onClick={() => setShowPassword((v) => !v)}
                                className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600"
                                title={showPassword ? 'Hide' : 'Show'}>
                                <Icon icon={showPassword ? 'mdi:eye-off-outline' : 'mdi:eye-outline'} className="tw-text-sm" />
                            </button>
                        </div>
                    </Field>

                    <Field label="From email" required error={errors.from_email}>
                        <input type="email" className={inputCls(errors.from_email)} maxLength={255} disabled={saving}
                            placeholder="billing@example.com"
                            value={form.from_email} onChange={(e) => set('from_email', e.target.value)} />
                    </Field>

                    <Field label="From name" error={errors.from_name}>
                        <input className={inputCls(errors.from_name)} maxLength={150} disabled={saving}
                            value={form.from_name} onChange={(e) => set('from_name', e.target.value)} />
                    </Field>

                    <Field label="Reply-to email" error={errors.reply_to_email} className="sm:tw-col-span-2">
                        <input type="email" className={inputCls(errors.reply_to_email)} maxLength={255} disabled={saving}
                            value={form.reply_to_email} onChange={(e) => set('reply_to_email', e.target.value)} />
                    </Field>

                    <div className="sm:tw-col-span-2 tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-3 tw-pt-1">
                        <ToggleSwitch id="smtp-is-default" checked disabled
                            label="Set as default" description="Replaces the society's current default account" />
                        <ToggleSwitch id="smtp-is-active" checked={form.is_active === 1} disabled={saving}
                            onChange={(v) => set('is_active', v ? 1 : 0)}
                            label="Active" description="Inactive accounts are kept but not used" />
                    </div>
                </div>
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
                <button type="button" onClick={onClose} disabled={saving}
                    className="tw-h-8 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50 disabled:tw-opacity-50">
                    Cancel
                </button>
                <button type="button" onClick={handleSubmit} disabled={saving || unchanged} style={{ marginLeft: 0 }}
                    className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
                    {saving && <Icon icon="mdi:loading" className="tw-text-sm tw-animate-spin" />}
                    {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Account'}
                </button>
            </DialogActions>
        </Dialog>
    );
};

export default SmtpConfigFormModal;
